(ns arthur.subs.ui
  "Layer-2 extractors over the editor's own state, plus the one layer-3 that
  resolves a selection to the thing it names.

  Cheap by construction, like `subs/playback`: each reads a path and returns a
  value, so clicking a swatch notifies the swatches and nothing else."
  (:require [arthur.domain.creation :as creation]
            [arthur.domain.clip :as clip]
            [arthur.domain.gesture :as gesture]
            [arthur.domain.nest :as nest]
            [arthur.domain.node :as node]
            [arthur.domain.pick :as pick]
            [arthur.domain.symbol :as symbol]
            [arthur.events.edit :as edit]
            [arthur.footage.store :as store]
            [arthur.subs.playback :as playback]
            [arthur.subs.render :as render]
            [re-frame.core :as rf]))

(rf/reg-sub ::selection (fn [db _] (get-in db [:ui :selection])))
(rf/reg-sub
 ::selections
 (fn [db _]
   (let [primary (get-in db [:ui :selection])
         many    (vec (get-in db [:ui :selections]))]
     ;; Older structural commands replace `:selection` directly. Treat that as
     ;; an intentional single selection unless it is still the primary member
     ;; of the multi-selection.
     (cond (nil? primary) []
           (some #{primary} many) many
           :else [primary]))))
(rf/reg-sub ::clipboard (fn [db _] (get-in db [:ui :clipboard])))
(rf/reg-sub ::retry (fn [db _] (get-in db [:ui :retry])))
(rf/reg-sub ::tone      (fn [db _] (get-in db [:ui :tone])))
(rf/reg-sub ::read-only? (fn [db _] (edit/read-only? db)))
;; Looking is all a read-only project allows, so it is all the stage does.
(rf/reg-sub ::tool      (fn [db _] (if (edit/read-only? db) :select (or (get-in db [:ui :tool]) :select))))
(rf/reg-sub ::auto-key? (fn [db _] (boolean (get-in db [:ui :auto-key?]))))
(rf/reg-sub ::draft     (fn [db _] (get-in db [:ui :draft])))
(rf/reg-sub ::vertex    (fn [db _] (get-in db [:ui :vertex])))
(rf/reg-sub ::hover     (fn [db _] (get-in db [:ui :hover])))
(rf/reg-sub ::brush     (fn [db _] (get-in db [:ui :brush] 6)))
(rf/reg-sub ::fit   (fn [db _] (get-in db [:ui :fit] 1)))
(rf/reg-sub ::last-op   (fn [db _] (get-in db [:ui :last])))

(rf/reg-sub ::convert   (fn [db _] (get-in db [:ui :convert])))
(rf/reg-sub ::drop      (fn [db _] (get-in db [:ui :drop])))
(rf/reg-sub ::tabs      (fn [db _] (get-in db [:ui :tabs])))
(rf/reg-sub ::expanded  (fn [db _] (get-in db [:ui :expanded])))
(rf/reg-sub ::knobs     (fn [db _] (get-in db [:ui :knobs])))

(rf/reg-sub
 ::last
 :<- [::last-op]
 :<- [::render/clip-id]
 :<- [::render/paint-revision]
 ;; The last stroke while the document is still what it left: any other edit,
 ;; or an undo, takes the panel away, as Blender's goes with the next operation.
 (fn [[op id _] _]
   (when (and op (identical? (:after op) (:clip (store/entry id)))) op)))

(rf/reg-sub
 ::selected-node
 :<- [::selection]
 :<- [::render/clip]
 (fn [[selection clip] _]
   ;; `[timeline-id node-id node]`, or nil. Returned as a triple rather than as
   ;; the node alone because every caller that wants the node also wants to know
   ;; where it lives — an edit names the timeline, and a bare node has forgotten.
   (when (and clip (= :node (first selection)))
     (let [[_ sid id] selection]
       (when-let [n (get-in clip [:symbols sid :nodes id])]
         [sid id n])))))

(rf/reg-sub
 ::selected-local
 :<- [::selected-node]
 :<- [::selection]
 :<- [::render/clip]
 :<- [::render/clip-id]
 :<- [::render/open]
 :<- [::render/open-frame]
 (fn [[[_ id n] [_ _ _ path] clip clip-id open f] _]
   ;; `nest/inside` the selected node, from the open symbol: its own frame, the
   ;; matrix from its coordinates to the stage's, and the time map from the open
   ;; symbol's frames to its own. A selection made on the stage has no path and
   ;; names a node in the open symbol. From the clip as it is mid-drag, as
   ;; `::selected-placement` is, so the points go with the shape they belong to.
   (when n
     (nest/inside clip (:store (store/entry clip-id)) open (or path [id]) f))))

(rf/reg-sub
 ::own-time
 :<- [::render/clip-id]
 :<- [::render/open]
 :<- [::render/open-frame]
 (fn [[clip-id open f] [_ path]]
   ;; Row `path`'s own clock from the open symbol — `nest/own-time` — and the
   ;; frame of it the playhead is over.
   (let [{clip :clip st :store} (store/entry clip-id)]
     (when-let [t (and clip (seq path) (nest/own-time clip st open (vec path) f))]
       {:time t :frame (js/Math.floor (* (:rate t) (- f (:at t))))}))))

(rf/reg-sub
 ::creation-target
 :<- [::selection]
 :<- [::render/clip]
 :<- [::render/clip-id]
 :<- [::render/open]
 :<- [::render/open-frame]
 (fn [[selection clip clip-id open f] _]
   (when clip
     (creation/target clip (:store (store/entry clip-id)) open selection f))))

(rf/reg-sub
 ::onion-scope
 :<- [::selection]
 :<- [::render/clip]
 :<- [::render/clip-id]
 :<- [::render/open]
 :<- [::render/open-frame]
 ;; WHAT ONION SKIN GHOSTS: the selection's row, or the lane it is in. A cel,
 ;; and anything drawn in one, is on screen for its own few frames, so ghosting
 ;; it ghosts nothing either side — and drawing selects the shape just drawn,
 ;; which put the onion out at the first stroke. A lane's neighbours are its
 ;; other drawings, which is what onion skin is for. The nearest lane, so a
 ;; lane drawn inside a cel of another ghosts its own drawings.
 (fn [[[kind _ id path] clip clip-id open f] _]
   (if-not (= :node kind)
     []
     (let [path (vec (or path [id]))
           st (some-> clip-id store/entry :store)
           lane? #(when-let [{:keys [sid]} (and clip (nest/inside clip st open % f))]
                    (symbol/lane? (clip/symbol clip sid)))]
       (or (some #(let [up (pop %)] (when (and (seq up) (lane? up)) up))
                 (take-while seq (iterate pop path)))
           path)))))

(rf/reg-sub
 ::creation-placement
 :<- [::creation-target]
 :<- [::render/clip]
 :<- [::render/clip-id]
 :<- [::render/open]
 :<- [::render/open-frame]
 (fn [[{:keys [path]} clip clip-id open f] _]
   ;; The effective occurrence whose contents receive creation. A fallback may
   ;; therefore outline an ancestor while the off-frame primary selection stays
   ;; unchanged.
   (when (and clip (seq path))
     (let [st (:store (store/entry clip-id))]
       (when-let [{:keys [sid id frame] :as pl} (nest/placement clip st open path f)]
         (let [n (get-in clip [:symbols sid :nodes id])]
           (assoc pl :node n
                  ;; An explicitly created symbol can be empty. It is still a
                  ;; valid creation destination, so give its occurrence a
                  ;; visible target box using the source symbol's stage until
                  ;; content supplies tighter bounds. Drawing-created symbols
                  ;; already take the normal content-bounds branch.
                  :bounds (or ((pick/bounds-of clip st sid n) frame)
                              (when-let [source (node/source n)]
                                (let [[w h] (clip/stage clip source)]
                                  [0 0 w h]))))))))))

(rf/reg-sub
 ::selected-placement
 :<- [::selected-node]
 :<- [::selection]
 :<- [::render/clip]
 :<- [::render/clip-id]
 :<- [::render/open]
 :<- [::render/open-frame]
 (fn [[[_ id n] [_ _ _ path] clip clip-id open f] _]
   ;; `nest/placement` of the selected node, and `:bounds` around what it draws
   ;; in its own coordinates, for the stage's handles. From the clip as it is
   ;; mid-drag, so the handles go with what they move.
   (when n
     (let [st (:store (store/entry clip-id))]
       (when-let [pl (nest/placement clip st open (or path [id]) f)]
         (let [bounds ((pick/bounds-of clip st (:sid pl) n) (:frame pl))]
           (assoc pl :node n :bounds bounds
                  ;; WHERE THE CROSS GOES, in the space `:parent` takes to the
                  ;; stage: the node's own pivot, or the middle of what it draws
                  ;; for one nobody has pivoted. `gesture/pivot` is the one rule,
                  ;; so the cross marks the point a drag will actually turn
                  ;; about rather than a second guess at it.
                  :pivot (gesture/pivot (gesture/values n (:frame pl) st) bounds))))))))

(rf/reg-sub
 ::selected-placements
 :<- [::selections]
 :<- [::render/clip]
 :<- [::render/clip-id]
 :<- [::render/open]
 :<- [::render/open-frame]
 (fn [[selections clip clip-id open f] _]
   (let [st (:store (store/entry clip-id))]
     (into [] (keep (fn [[_ _ _ path :as selection]]
              (when (and clip (= :node (first selection)) (seq path))
                (when-let [{:keys [sid id] :as pl} (nest/placement clip st open path f)]
                  (when-let [n (get-in clip [:symbols sid :nodes id])]
                    (assoc pl :selection selection :path path :node n
                           :bounds ((pick/bounds-of clip st sid n) (:frame pl))))))))
           selections))))

(rf/reg-sub
 ::settled-clip
 :<- [::render/clip-id]
 :<- [::render/paint-revision]
 (fn [[id _] _]
   ;; The document AS WRITTEN, not as a drag currently has it — the same choice
   ;; `::render/sounds` makes, for a sharper version of the same reason.
   ;;
   ;; `::render/clip` yields a fresh document on every pointer move of a timeline
   ;; slide so the stage can follow it. The media pool draws its symbols as
   ;; RASTERISED PICTURES, so subscribing to that would re-resolve and re-encode
   ;; every thumbnail in the pool thirty times a second, to show a change no
   ;; thumbnail of a symbol has any way to show. Nothing the pool lists —
   ;; a symbol's name, its length, what places it — moves during a drag.
   (:clip (store/entry id))))

(rf/reg-sub
 ::project-footage
 :<- [::render/clip-id]
 :<- [::render/paint-revision]
 :<- [::playback/footage]
 (fn [[id _ {:keys [available uploaded]}] _]
   ;; THIS PROJECT's video: what the document was made from, what its sounds
   ;; play, and what was uploaded while it was open. Read off the document rather
   ;; than kept beside it, so a sound placed from footage makes that footage the
   ;; project's without anything else being told.
   (let [entry (store/entry id)
         used  (into (set uploaded)
                     (keep :footage (vals (get-in entry [:clip :subjects]))))
         used  (into used (for [[_ sym] (get-in entry [:clip :symbols])
                                [_ n] (:nodes sym)
                                :let [f (get-in n [:source :footage])]
                                :when f]
                            f))
         ;; And what a tracing layer shows.
         used  (into used (keep #(get-in % [:media :footage]))
                     (vals (get-in entry [:clip :symbols])))]
     (filterv #(contains? used (:id %)) available))))

(rf/reg-sub
 ::project-sounds
 :<- [::render/clip-id]
 :<- [::render/paint-revision]
 :<- [::playback/footage]
 (fn [[id _ {:keys [sounds uploaded]}] _]
   ;; As `::project-footage`: the sounds this document plays, and those uploaded
   ;; while it was open.
   (let [used (into (set uploaded)
                    (for [[_ sym] (get-in (store/entry id) [:clip :symbols])
                          [_ n] (:nodes sym)
                          :let [s (get-in n [:source :sound])]
                          :when s]
                      s))]
     (filterv #(contains? used (:id %)) sounds))))
